import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Plane, Map, Clock, CalendarDays, Tag, LayoutDashboard, Headphones, FileText } from "lucide-react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/for-businesses")({
  head: () => ({
    meta: [
      { title: "B2B Portal for Hotels & Travel Agencies | Bacha Way Travel" },
      { name: "description", content: "Hotels, riads, concierges and travel agencies in Morocco: centralise guest transfers with the Bacha Way B2B portal. Preferential rates, dashboard, monthly billing." },
      { property: "og:title", content: "B2B Portal for Hotels & Travel Agencies" },
      { property: "og:description", content: "Centralise guest transfers in Morocco. Preferential rates, dedicated dashboard, monthly billing." },
      { property: "og:url", content: "https://bachawaytravel.com/for-businesses" },
    ],
    links: [{ rel: "canonical", href: "https://bachawaytravel.com/for-businesses" }],
  }),
  component: ForBusinessesPage,
});

function ForBusinessesPage() {
  const { t } = useTranslation();
  const services = [
    { icon: Plane, title: t("business.s1t"), desc: t("business.s1d") },
    { icon: Map, title: t("business.s2t"), desc: t("business.s2d") },
    { icon: Clock, title: t("business.s3t"), desc: t("business.s3d") },
    { icon: CalendarDays, title: t("business.s4t"), desc: t("business.s4d") },
  ];
  const benefits = [
    { icon: Tag, title: t("business.b1t"), desc: t("business.b1d") },
    { icon: LayoutDashboard, title: t("business.b2t"), desc: t("business.b2d") },
    { icon: Headphones, title: t("business.b3t"), desc: t("business.b3d") },
    { icon: FileText, title: t("business.b4t"), desc: t("business.b4d") },
  ];
  return (
    <>
      <section className="bg-hero-navy text-white">
        <div className="mx-auto max-w-7xl px-6 py-20 md:py-28 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-4 py-1.5 text-[10px] md:text-xs uppercase tracking-[0.25em] text-white/80">
            {t("business.badge")}
          </div>
          <h1 className="mt-8 mx-auto max-w-3xl text-4xl md:text-6xl font-bold leading-[1.05]">
            {t("business.title1")} <span className="text-gradient-sky">{t("business.title2")}</span>
          </h1>
          <p className="mt-5 mx-auto max-w-2xl text-white/70">{t("business.lead")}</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/business-signup" className="rounded-full bg-white px-7 py-3.5 text-sm font-semibold text-primary hover:scale-[1.02] transition-transform">{t("business.signup")}</Link>
            <a href="#signin" className="rounded-full border border-white/30 bg-white/5 px-7 py-3.5 text-sm font-semibold text-white hover:bg-white/10">{t("business.signin")}</a>
          </div>
        </div>
      </section>

      <section className="py-24 bg-background">
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-xs uppercase tracking-[0.25em] text-primary font-semibold">{t("business.servicesEyebrow")}</div>
          <h2 className="mt-3 text-3xl md:text-5xl font-bold">{t("business.servicesTitle")}</h2>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {services.map((s) => (
              <div key={s.title} className="rounded-2xl border border-border bg-card p-7 hover:border-primary/40 hover:shadow-xl hover:shadow-primary/5 transition-all">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary"><s.icon className="h-6 w-6" /></div>
                <div className="mt-5 text-lg font-semibold">{s.title}</div>
                <p className="mt-2 text-sm text-muted-foreground">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-secondary/40">
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-xs uppercase tracking-[0.25em] text-primary font-semibold">{t("business.benefitsEyebrow")}</div>
          <h2 className="mt-3 text-3xl md:text-5xl font-bold">{t("business.benefitsTitle")}</h2>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {benefits.map((b) => (
              <div key={b.title} className="rounded-2xl border border-border bg-card p-7">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary text-primary-foreground"><b.icon className="h-6 w-6" /></div>
                <div className="mt-5 text-lg font-semibold">{b.title}</div>
                <p className="mt-2 text-sm text-muted-foreground">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="signin" className="py-24 bg-background">
        <div className="mx-auto max-w-5xl px-6">
          <div className="rounded-3xl bg-hero-navy text-white p-10 md:p-16 text-center">
            <h2 className="text-3xl md:text-5xl font-bold">{t("business.joinTitle")}</h2>
            <p className="mt-4 text-white/70 max-w-xl mx-auto">{t("business.joinLead")}</p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/business-signup" className="rounded-full bg-white px-7 py-3.5 text-sm font-semibold text-primary inline-flex items-center gap-2">{t("business.become")} <ArrowRight className="h-4 w-4" /></Link>
              <Link to="/contact" className="rounded-full border border-white/30 bg-white/5 px-7 py-3.5 text-sm font-semibold text-white">{t("business.already")}</Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
